<template>
  <div class="home-view">
    <!-- ── HERO · centered (HomeA) ───────────────────────────────────────── -->
    <section class="hero tex-panel tex-grain">
      <div class="hero__wash" aria-hidden="true" />

      <div class="hero__inner">
        <AlmanacMark :scale="0.7" />

        <h1 class="hero__wordmark">
          <span class="hero__wordmark-mi">MI</span> EUCHRE
        </h1>

        <p class="hero__subhead">
          Deal a private game with your crew — or climb the ranked ladder in public play and
          sponsor-backed tournaments.
        </p>

        <p
          v-if="signInErrorMessage"
          role="alert"
          class="sign-in-error font-sans"
          data-testid="sign-in-error"
        >
          {{ signInErrorMessage }}
        </p>

        <div class="cta-buttons">
          <Button size="lg" class="btn-play" @click="onCreateAccount">
            {{ authStore.isAuthenticated ? 'Play Now' : 'Create free account' }}
          </Button>
          <Button size="lg" variant="secondary" class="btn-rules" @click="goToLobby">
            Browse public games
          </Button>
        </div>

        <!-- HeroTrust -->
        <div class="hero-trust">
          <div class="hero-trust__avatars">
            <div
              v-for="(cfg, i) in trustAvatars"
              :key="i"
              class="hero-trust__avatar"
            >
              <Avatar class="hero-trust__ava">
                <AvatarFallback style="background: transparent">
                  <Avataaar v-bind="cfg" />
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
          <span class="hero-trust__copy font-sans">
            Free to start — <strong>3 private games a month</strong>, no card required.
          </span>
        </div>
      </div>

      <Treeline />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'
import { Button, Avatar, AvatarFallback } from '@dcs/ui'
import Avataaar from '@/components/Avataaar.vue'
import AlmanacMark from '@/components/brand/AlmanacMark.vue'
import Treeline from '@/components/brand/marks/TreeLine.vue'
import { avatarConfigFromName } from '@/lib/seededAvatar'
import { SAMPLE_HERO_TRUST_SEEDS } from '@/mocks/sampleCompetitive'

// ── SEO ──────────────────────────────────────────────────────────────────────
const siteUrl = 'https://mieuchre.com'
const siteName = 'MI Euchre'
const title = 'MI Euchre | Play Michigan Euchre Online Free with Friends'
const description =
  'Play authentic Michigan-style Euchre online for free with friends. Real-time multiplayer card game with custom avatars, Google login, public play, ranked ladder, and sponsor-backed tournaments. No downloads required - start playing now!'
const keywords =
  'euchre online, michigan euchre, play euchre free, multiplayer euchre, card game online, euchre with friends, real-time card game, ranked euchre, euchre tournaments'
const ogImage = `${siteUrl}/hero-up-north.webp`

useHead({
  title,
  meta: [
    { name: 'description', content: description },
    { name: 'keywords', content: keywords },
    { name: 'author', content: 'Duff Cloud Services' },
    {
      name: 'robots',
      content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: siteUrl },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:type', content: 'image/webp' },
    { property: 'og:image:alt', content: 'MI Euchre - Play Michigan Euchre Online' },
    { property: 'og:site_name', content: siteName },
    { property: 'og:locale', content: 'en_US' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:url', content: siteUrl },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: ogImage },
    { name: 'twitter:image:alt', content: 'MI Euchre - Play Online' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
    { name: 'apple-mobile-web-app-title', content: 'MI Euchre' },
    { name: 'theme-color', content: '#2F4636' },
    { name: 'mobile-web-app-capable', content: 'yes' },
  ],
  link: [
    { rel: 'canonical', href: siteUrl },
    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'VideoGame',
        name: siteName,
        description,
        url: siteUrl,
        gamePlatform: 'Web Browser',
        genre: 'Card Game',
        playMode: 'MultiPlayer',
        numberOfPlayers: { '@type': 'QuantitativeValue', minValue: 2, maxValue: 4 },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
        },
        applicationCategory: 'Game',
        operatingSystem: 'Any',
        author: {
          '@type': 'Organization',
          name: 'Duff Cloud Services',
          url: 'https://duffcloudservices.com',
        },
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteName,
        url: siteUrl,
        description,
        potentialAction: {
          '@type': 'PlayAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: siteUrl,
            actionPlatform: [
              'http://schema.org/DesktopWebPlatform',
              'http://schema.org/MobileWebPlatform',
            ],
          },
        },
      }),
    },
  ],
  htmlAttrs: { lang: 'en', dir: 'ltr' },
  bodyAttrs: { class: 'home-page' },
})

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const profileStore = useProfileStore()

const trustAvatars = computed(() => SAMPLE_HERO_TRUST_SEEDS.map((seed) => avatarConfigFromName(seed)))

/**
 * `?error=` codes the server's Euchre Google callback sends to this page
 * (server/internal/handlers/euchre.go), and what the player reads. An unknown
 * code shows nothing.
 */
const SIGN_IN_ERROR_MESSAGES: ReadonlyMap<string, string> = new Map([
  // C-2257 / C-2457: Google has not verified the email address on the Google
  // account, so the server refused the sign-in and created no session.
  [
    'google_email_unverified',
    "We couldn't sign you in with Google because Google hasn't verified the email address on your Google account. Verify the address with Google, then sign in again.",
  ],
])

const signInErrorMessage = computed<string | null>(() => {
  const code = typeof route.query.error === 'string' ? route.query.error : ''
  return SIGN_IN_ERROR_MESSAGES.get(code) ?? null
})

onMounted(async () => {
  await authStore.checkSession()
  if (authStore.isAuthenticated && route.query.returnTo) {
    router.push(route.query.returnTo as string)
    return
  }
  if (authStore.isAuthenticated && !profileStore.isLoaded && !profileStore.isLoading) {
    try {
      await profileStore.fetchProfile()
    } catch (error) {
      console.error('Failed to load profile for HomeView:', error)
    }
  }
})

function onCreateAccount() {
  if (authStore.isAuthenticated) {
    goToLobby()
    return
  }
  const returnTo = (route.query.returnTo as string) || '/lobby'
  authStore.login(returnTo)
}

function goToLobby() {
  router.push('/lobby')
}
</script>

<style scoped>
.home-view {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: rgb(var(--ink));
  background: rgb(var(--paper));
}

/* ── HERO · centered (HomeA) ──────────────────────────────────────────── */
.hero {
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: min(82vh, 760px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem clamp(1.25rem, 5vw, 4.1rem) 8.5rem;
}
.hero__wash {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: radial-gradient(
    120% 76% at 50% 32%,
    rgba(243, 236, 218, 0.48),
    rgba(33, 52, 42, 0.08) 56%,
    rgba(33, 52, 42, 0.46)
  );
}
.hero__inner {
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.1rem;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}
.hero__wordmark {
  font-family: var(--font-display);
  font-size: clamp(3rem, 9vw, 4.9rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
  color: rgb(var(--ink));
  margin: 0.5rem 0 0;
  text-shadow: 0 1px 0 rgba(243, 236, 218, 0.5);
}
.hero__wordmark-mi {
  color: rgb(var(--barn));
}
.hero__subhead {
  font-family: var(--font-display);
  font-size: clamp(1.05rem, 2vw, 1.4375rem);
  font-weight: 500;
  color: rgb(var(--walnut-deep));
  max-width: 660px;
  line-height: 1.35;
  margin: 0;
}
.sign-in-error {
  max-width: 560px;
  margin: 0;
  padding: 0.75rem 1rem;
  border: 1px solid rgb(var(--barn));
  border-radius: 0.5rem;
  background: rgb(var(--paper));
  color: rgb(var(--barn-deep));
  font-size: 0.95rem;
  line-height: 1.4;
}
.cta-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.7rem;
}
.btn-play {
  background: rgb(var(--barn));
  color: rgb(var(--paper));
  font-size: 1.05rem;
  font-weight: 700;
  padding: 1.4rem 2.1rem;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 4px 0 rgb(var(--barn-deep)), 0 10px 22px rgba(33, 52, 42, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-play:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 0 rgb(var(--barn-deep)), 0 14px 26px rgba(33, 52, 42, 0.35);
}
.btn-rules {
  background: rgba(243, 236, 218, 0.92);
  color: rgb(var(--walnut-deep));
  font-size: 1.05rem;
  font-weight: 600;
  padding: 1.4rem 1.9rem;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 4px 0 rgba(94, 55, 36, 0.28);
  transition: transform 0.2s ease;
}
.btn-rules:hover {
  transform: translateY(-2px);
}

/* HeroTrust */
.hero-trust {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 0.6rem;
  background: rgba(43, 37, 25, 0.06);
  padding: 8px 18px 8px 10px;
  border-radius: 30px;
}
.hero-trust__avatars {
  display: flex;
}
.hero-trust__avatar {
  margin-left: -10px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(243, 236, 218, 1);
  background: rgb(var(--paper));
}
.hero-trust__avatar:first-child {
  margin-left: 0;
}
.hero-trust__ava {
  width: 30px;
  height: 30px;
}
.hero-trust__copy {
  font-size: 0.8125rem;
  color: rgb(var(--ink));
}
.hero-trust__copy strong {
  color: rgb(var(--barn));
}

/* ── Responsive ───────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .hero {
    min-height: min(86vh, 680px);
    padding: 2rem 1.25rem 7rem;
  }
  .hero__inner {
    gap: 0.9rem;
  }
}
</style>
